import React, { useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import LayersHeading from 'components/customComponents/Layers/LayersHeading';
import ConditionCard from './ConditionCard';
import {
  OptionsGroupsVertical,
  RadioGroups,
} from 'components/customComponents/Layers/InputGroups';
import LayerInfo from 'components/customComponents/Layers/LayerInfo';
import {
  ConditionDTO,
  ConditionIngredientDTO,
  CustomLayerDTO,
  LayerDTO,
  LayerTypeDTO,
} from 'business/models/Models';
import { usePreserveExperiment, useRMDispatch, useRMSelector } from 'redux/hooks';
import {
  DisplayModes,
  getNewId,
  GetDisplayMode,
  isNullOrUndefined,
  LayerDesignTabs,
} from 'utils/Global';
import LayersWarning from 'components/customComponents/Layers/LayersWarning';
import {
  selectIsEditable,
  selectIsModifyAccessRights,
} from 'redux/selectors/StateSelectors';
import {
  setWellNavigationDetails,
  updateExperimentLayers,
} from 'redux/reducers/experimentDataReducer';
import {
  selectExp,
  selectExperimentLayers,
  selectSelectedLayer,
  selectSelectedLayerId,
} from 'redux/selectors/ExperimentStateSelector';
import { cloneDeep } from 'lodash';
import { useIsScreenInUse } from 'redux/hooks/common';
import {
  useAllowLayerEditing,
  useLastSelectedWellNavigationDetails,
} from 'redux/hooks/experiment';
import {
  getConditionNumberForWell,
  getWellNavigationDetailsForConditionNumber,
} from 'business/plateviewer/Generate/GenerateCommon';

export default function LayerTabContent({ layerData }) {
  const [layerName, setLayerName] = useState(layerData.name);
  const [layerVolume, setLayerVolume] = useState(layerData.volume);
  const [layerTarget, setLayerTarget] = useState(layerData.targetDrop);

  useEffect(() => {
    setLayerName(layerData.name);
    setLayerVolume(layerData.volume);
    setLayerTarget(layerData.targetDrop);
  }, [layerData]);

  return (
    <>
      <LayerInfo
        layerName={layerName}
        setLayerName={setLayerName}
        layerVolume={layerVolume}
        layerTarget={layerTarget}
        setLayerVolume={setLayerVolume}
        setLayerTarget={setLayerTarget}
      />
      <LayersHeading text="Advance Options" />
      <div
        id="commandpanel-customlayercontent-wrapper"
        className="flex flex-space-evenly w-100pr"
      >
        <div
          id="commandpanel-customlayercontent-options"
          className="flex w-40 flex-column flex-space-between"
        >
          <LayersHeading text="Layer Options" />
          <OptionsGroupsVertical label1="Override" label2="Locked" />
        </div>
        <div
          id="commandpanel-customlayercontent-concentration"
          className="flex w-50 flex-column flex-space-between"
          style={{ paddingLeft: '2%' }}
        >
          <LayersHeading text="Concentration" />
          <RadioGroups
            label1="Volume Base Addition"
            label2="Concentration Base Addition"
          />
        </div>
      </div>

      <div
        id="commandpanel-customlayercontent-iterativescreen"
        className="flex flex-space-between"
        style={{}}
      >
        <LayersHeading
          text="Iterative Screen Info"
          style={{ margin: '12px 0 !important' }}
        />
        <LayersHeading text="Round:0" />
      </div>
      <div id="commandpanel-customlayercontent-input" className="flex w-100pr ml-40">
        <div
          id="commandpanel-customlayercontent-inputform"
          className="flex w-50 flex-column"
          style={{ marginLeft: '30px', fontSize: '0.8125rem' }}
        >
          <label id="commandpanel-customlayercontent-inputform1">Optimized form:</label>
          <label id="commandpanel-customlayercontent-inputform2">
            Iterative Step Size:
          </label>
        </div>
      </div>
    </>
  );
}

export type ConditionTabContentProps = {
  setContentView: (abc: any) => void;
  setEditIngredientData?: any;
  setSelectedIngredient?: any;
  setSelectedTab?: (tabIndex: number) => void;
};

const conditionListScrollByLayerId = new Map<string | number, number>();

export function ConditionTabContent({
  setContentView,
  setEditIngredientData,
  setSelectedIngredient,
  setSelectedTab,
}: ConditionTabContentProps) {
  const [layerConditionsList, setLayerConditionsList] = useState<ConditionDTO[]>([]);
  const [highlightedConditionNumber, setHighlightedConditionNumber] = useState<
    number | null
  >(null);

  const [buffer, setBuffer] = useState<ConditionDTO | undefined>(undefined);
  const _selectedLayer = useRMSelector(selectSelectedLayer);
  const _layers = useRMSelector(selectExperimentLayers);
  const _selectedLayerId = useRMSelector(selectSelectedLayerId);
  const exp = useRMSelector(selectExp);
  const lastSelectedWell = useLastSelectedWellNavigationDetails();
  const [conditionsCount, setConditionsCount] = useState<string>(
    computeConditionsCount(),
  );
  const [customLayer, setCustomLayer] = useState<CustomLayerDTO>();
  const dispatch = useRMDispatch();
  const _setLayers = useCallback(
    (layers: LayerDTO[]) => dispatch(updateExperimentLayers(layers)),
    [dispatch],
  );
  const callPreserveUndoableState = usePreserveExperiment();
  const isEditMode: boolean = useRMSelector(selectIsEditable);
  const modifyAccessRights: boolean = useRMSelector(selectIsModifyAccessRights);
  const isScreenInUse: boolean = useIsScreenInUse();
  const isLayerEditingAllowed = useAllowLayerEditing();
  const conditionsContainerRef = useRef<HTMLDivElement | null>(null);
  const selectedLayerIdRef = useRef<string | number | undefined>(_selectedLayerId);
  const skipScrollRestoreRef = useRef(false);

  const scrollToConditionCard = useCallback((conditionNumber: number) => {
    const container = conditionsContainerRef.current;
    const conditionElement = container?.querySelector(
      `[data-condition-number="${conditionNumber}"]`,
    ) as HTMLElement | null;

    if (!container || !conditionElement) {
      return;
    }

    conditionElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, []);

  const handleConditionSelect = useCallback(
    (conditionNumber: number) => {
      if (!exp || !_selectedLayer || GetDisplayMode() !== DisplayModes.DESIGN) {
        return;
      }

      const wellDetails = getWellNavigationDetailsForConditionNumber(
        _selectedLayer,
        conditionNumber,
        exp.containerRows,
        exp.containerCols,
        exp.plateDto,
      );

      if (!wellDetails) {
        return;
      }

      setHighlightedConditionNumber(conditionNumber);
      dispatch(setWellNavigationDetails([wellDetails]));
    },
    [dispatch, exp, _selectedLayer],
  );

  const persistConditionScrollPosition = useCallback(() => {
    selectedLayerIdRef.current = _selectedLayerId;
    const layerId = selectedLayerIdRef.current;
    const container = conditionsContainerRef.current;
    if (!layerId || !container) {
      return;
    }
    conditionListScrollByLayerId.set(layerId, container.scrollTop);
  }, [_selectedLayerId]);

  useEffect(() => {
    const layers = cloneDeep(_layers);
    const selectedLayer = layers.find((lyr) => lyr.id === _selectedLayer.id);
    setCustomLayer(selectedLayer.layerDetail as CustomLayerDTO);
  }, [_selectedLayerId]);

  function sortConditionsList() {
    const layers = cloneDeep(_layers);
    const selectedLayer = layers.find((lyr) => lyr.id === _selectedLayer.id);
    const layer = cloneDeep(selectedLayer);
    const sortedConditionsList = (layer.layerDetail as CustomLayerDTO).condition.sort(
      (c1, c2) => c1.conditionNumber - c2.conditionNumber,
    );
    setLayerConditionsList(sortedConditionsList);
  }

  useEffect(() => {
    sortConditionsList();
  }, [_layers, _selectedLayerId]);

  useLayoutEffect(() => {
    if (!_selectedLayerId) {
      return;
    }

    if (skipScrollRestoreRef.current) {
      skipScrollRestoreRef.current = false;
      return;
    }

    const container = conditionsContainerRef.current;
    const savedScrollTop = conditionListScrollByLayerId.get(_selectedLayerId);
    if (container && typeof savedScrollTop === 'number') {
      container.scrollTop = savedScrollTop;
    }
  }, [_selectedLayerId, layerConditionsList.length]);

  useEffect(() => {
    const container = conditionsContainerRef.current;
    if (!_selectedLayerId || !container) {
      return;
    }

    container.addEventListener('scroll', persistConditionScrollPosition);

    return () => {
      persistConditionScrollPosition();
      container.removeEventListener('scroll', persistConditionScrollPosition);
    };
  }, [_selectedLayerId, persistConditionScrollPosition]);

  useEffect(() => {
    return () => {
      // Explicit unmount persistence for view switches.
      persistConditionScrollPosition();
    };
  }, [persistConditionScrollPosition]);

  useEffect(() => {
    if (
      !lastSelectedWell ||
      !_selectedLayer ||
      _selectedLayer.type !== LayerTypeDTO.ConditionList ||
      !exp ||
      GetDisplayMode() !== DisplayModes.DESIGN
    ) {
      return;
    }

    const conditionNumber = getConditionNumberForWell(
      _selectedLayer,
      lastSelectedWell.wellNum,
      lastSelectedWell.plateNum,
      exp.containerRows,
      exp.containerCols,
      exp.plateDto,
    );

    if (
      !conditionNumber ||
      !layerConditionsList.some(
        (condition) => condition.conditionNumber === conditionNumber,
      )
    ) {
      setHighlightedConditionNumber(null);
      return;
    }

    setHighlightedConditionNumber(conditionNumber);
    skipScrollRestoreRef.current = true;
    setSelectedTab?.(LayerDesignTabs.Protein);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollToConditionCard(conditionNumber));
    });
  }, [
    lastSelectedWell?.wellNum,
    lastSelectedWell?.plateNum,
    _selectedLayer?.id,
    _selectedLayer?.x,
    _selectedLayer?.y,
    _selectedLayer?.width,
    _selectedLayer?.height,
    exp?.containerRows,
    exp?.containerCols,
    layerConditionsList.length,
    scrollToConditionCard,
    setSelectedTab,
  ]);

  function swapConditions(condNum1: number, condNum2: number) {
    const layers = cloneDeep(_layers);
    const customLayerIndex = layers.findIndex((lyr) => lyr.id === customLayer.layerId);
    const tempCustomLayer = layers[customLayerIndex].layerDetail as CustomLayerDTO;

    const cond1 = tempCustomLayer.condition.find((c) => c.conditionNumber == condNum1);
    const cond2 = tempCustomLayer.condition.find((c) => c.conditionNumber == condNum2);
    cond1.conditionNumber = condNum2;
    cond2.conditionNumber = condNum1;

    sortConditionsList();

    layers[customLayerIndex].dirty = true;
    callPreserveUndoableState();
    _setLayers(layers);
  }
  function handleChangeConditionsCount(e) {
    setConditionsCount(e.target.value);
  }
  function getMaxCondCount(): number {
    if (!_selectedLayer) {
      return 0;
    }
    return (_selectedLayer.width + 1) * (_selectedLayer.height + 1);
  }

  function getAvailableCondCount(): number {
    const customLayer: CustomLayerDTO = _selectedLayer.layerDetail as CustomLayerDTO;
    return customLayer.condition.length;
  }

  function onDeleteCondition(condition: ConditionDTO) {
    const layers = cloneDeep(_layers);
    const customLayerIndex = layers.findIndex((lyr) => lyr.id === customLayer.layerId);
    const tempCustomLayer = layers[customLayerIndex].layerDetail as CustomLayerDTO;

    const cond = tempCustomLayer.condition.find((c) => c.id === condition.id);
    if (!cond) {
      return;
    }
    const index = tempCustomLayer.condition.indexOf(cond);
    tempCustomLayer.condition.splice(index, 1);

    updateConditionNumbers(cond.conditionNumber, true, tempCustomLayer);

    layers[customLayerIndex].layerDetail = tempCustomLayer;
    layers[customLayerIndex].dirty = true;
    callPreserveUndoableState();
    _setLayers(layers);
  }

  function onCopyCondition(condition: ConditionDTO) {
    if (isNullOrUndefined(buffer) || condition.id !== buffer.id) {
      const clone = JSON.parse(JSON.stringify(condition));
      setBuffer(clone);
    } else if (!isNullOrUndefined(buffer) && condition.id === buffer.id) {
      setBuffer(undefined);
    }
  }

  function onPasteCondition(condition: ConditionDTO) {
    if (buffer == undefined) {
      return;
    }
    const ingsToPaste: ConditionIngredientDTO[] = [];
    for (const condIng of buffer.conditionIngredient) {
      const existingIng = condition.conditionIngredient.find(
        (cIng) =>
          cIng.ingredientIngredientTypeId === condIng.ingredientIngredientTypeId &&
          cIng.ingredientStockId === condIng.ingredientStockId,
      );
      if (!isNullOrUndefined(existingIng)) {
        existingIng.concentration += condIng.concentration;
      } else {
        const newCondIng = getCondIngCopy(condition, condIng);
        ingsToPaste.push(newCondIng);
      }
    }

    const layers = cloneDeep(_layers);
    const customLayerIndex = layers.findIndex((lyr) => lyr.id === customLayer.layerId);
    const tempCustomLayer = layers[customLayerIndex].layerDetail as CustomLayerDTO;

    tempCustomLayer.condition.forEach((cond) => {
      if (cond.conditionNumber == condition.conditionNumber) {
        cond.conditionIngredient.push(...ingsToPaste);
      }
    });

    layers[customLayerIndex].dirty = true;
    callPreserveUndoableState();
    _setLayers(layers);
  }

  function getCondIngCopy(condition: ConditionDTO, condIng: ConditionIngredientDTO) {
    const newCondIng = new ConditionIngredientDTO();
    newCondIng.conditionId = condition.id;
    newCondIng.ingredientIngredientTypeId = condIng.ingredientIngredientTypeId;
    newCondIng.ingredientStockId = condIng.ingredientStockId;
    newCondIng.highPhingredientStockId = condIng.highPhingredientStockId;
    newCondIng.concentration = condIng.concentration;
    newCondIng.pH = condIng.pH;
    newCondIng.id = getNewId();
    return newCondIng;
  }

  function updateConditionNumbers(
    pivotConditionNumber: number,
    moveUp: boolean,
    customLayer: CustomLayerDTO,
  ) {
    const condsToMove = getConditionsToMove(pivotConditionNumber, customLayer);
    let moveStep = 1;

    if (moveUp == true) {
      moveStep = -1;
    }

    for (const cond of condsToMove) {
      cond.conditionNumber += moveStep;
    }
  }

  function getConditionsToMove(
    pivotConditionNumber: number,
    customLayer: CustomLayerDTO,
  ): ConditionDTO[] {
    let condsToMove: ConditionDTO[] = customLayer.condition.filter(
      (c) => c.conditionNumber > pivotConditionNumber,
    );

    condsToMove = condsToMove.sort((c1, c2) => c1.conditionNumber - c2.conditionNumber);
    return condsToMove;
  }

  function insertCondition(targetCondNum = 0) {
    if (getMaxCondCount() === getAvailableCondCount()) {
      return;
    }

    const layers = cloneDeep(_layers);
    const customLayerIndex = layers.findIndex((lyr) => lyr.id === customLayer.layerId);
    const tempCustomLayer = layers[customLayerIndex].layerDetail as CustomLayerDTO;

    const cond: ConditionDTO = new ConditionDTO();
    cond.id = getNewId();
    cond.conditionListLayerId = tempCustomLayer.id;
    cond.conditionIngredient = [];
    updateConditionNumbers(targetCondNum, false, tempCustomLayer);
    cond.conditionNumber = targetCondNum + 1;
    tempCustomLayer.condition.push(cond);
    layers[customLayerIndex].dirty = true;
    setConditionsCount(computeConditionsCount());
    callPreserveUndoableState();
    _setLayers(layers);
  }

  function updateLayers() {
    const layers = cloneDeep(_layers);
    const customLayerIndex = layers.findIndex((lyr) => lyr.id === customLayer.layerId);
    layers[customLayerIndex].layerDetail = cloneDeep(customLayer) as CustomLayerDTO;
    layers[customLayerIndex].dirty = true;
    _setLayers(layers);
  }
  function addConditions() {
    if (conditionsCount === '') {
      return;
    }

    const condsCount: number = parseInt(conditionsCount.toString());

    if (getMaxCondCount() < getAvailableCondCount() + condsCount) {
      return;
    }

    const layers = cloneDeep(_layers);
    const customLayerIndex = layers.findIndex((lyr) => lyr.id === customLayer.layerId);
    const tempCustomLayer = layers[customLayerIndex].layerDetail as CustomLayerDTO;

    let highestCondNumber = tempCustomLayer.condition
      ? tempCustomLayer.condition.length
      : 0;

    for (let i = 0; i < condsCount; i++) {
      const cond: ConditionDTO = new ConditionDTO();
      cond.id = getNewId();
      cond.conditionListLayerId = customLayer.id;
      cond.conditionIngredient = [];
      cond.conditionNumber = ++highestCondNumber;

      tempCustomLayer.condition.push(cond);
    }
    layers[customLayerIndex].layerDetail = tempCustomLayer;
    layers[customLayerIndex].dirty = true;
    callPreserveUndoableState();
    _setLayers(layers);
    setConditionsCount('1');
  }

  function computeConditionsCount() {
    if (getMaxCondCount() > getAvailableCondCount()) {
      return '1';
    }
    return '0';
  }

  function getCondtionsView() {
    const conditionsView = layerConditionsList.map((condition) => (
      <ConditionCard
        key={`conditionCard-${condition.id}`}
        condition={condition}
        setContentView={setContentView}
        setEditIngredientData={setEditIngredientData}
        setSelectedIngredient={setSelectedIngredient}
        delConditionDelegate={onDeleteCondition}
        copyConditionDelegate={onCopyCondition}
        pasteConditionDelegate={onPasteCondition}
        buffer={buffer}
        insertConditionDelegate={insertCondition}
        swapConditionsDelegate={swapConditions}
        copyActive={!isNullOrUndefined(buffer) && buffer.id == condition.id}
        refreshUI={updateLayers}
        isEditMode={isEditMode && modifyAccessRights && isLayerEditingAllowed}
        persistConditionScrollPosition={persistConditionScrollPosition}
        isHighlighted={highlightedConditionNumber === condition.conditionNumber}
        onConditionSelect={handleConditionSelect}
      />
    ));
    return conditionsView;
  }

  function getMoreConditionsErrorUI() {
    const condsCount: number = parseInt(conditionsCount.toString());
    if (getMaxCondCount() < getAvailableCondCount() + condsCount) {
      return <LayersWarning errorText="Can't add conditions over than layer size" />;
    }

    return <></>;
  }

  const isAddbuttonDisabled = !(getMaxCondCount() > getAvailableCondCount());
  return (
    <>
      <div
        className="flex flex-column w-full"
        style={{ padding: '4px', marginTop: '8px', borderRadius: '4px' }}
      >
        <div className="flex " style={{ borderRadius: '4px' }}>
          <div style={{ width: '45%' }}>
            <label>Max conditions : {getMaxCondCount()}</label>
            <label>
              Available conditions :
              {' ' + Math.max(0, getMaxCondCount() - getAvailableCondCount())}
            </label>
          </div>

          <div className="flex flex-space-between flex-y-center" style={{ width: '55%' }}>
            {' '}
            {isEditMode &&
              modifyAccessRights &&
              !isScreenInUse &&
              isLayerEditingAllowed && (
                <button
                  onClick={addConditions}
                  disabled={isAddbuttonDisabled}
                  style={{
                    background: '#008F40',
                    opacity: isAddbuttonDisabled ? 0.3 : 1,
                    borderRadius: '0.3rem',
                    fontSize: '1rem',
                    lineHeight: '1rem',
                    minHeight: '1rem',
                    maxHeight: '1rem',
                    height: '1rem',
                    width: '90%',
                    letterSpacing: '0.078rem',
                    padding: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: 'white',
                    border: 'none',
                    outline: 'none',
                  }}
                >
                  ADD{' '}
                  <input
                    type="text"
                    size={1}
                    value={conditionsCount}
                    onClick={(e) => e.stopPropagation()}
                    onChange={handleChangeConditionsCount}
                    style={{
                      height: '2rem',
                      width: '2.25rem',
                      margin: '0.75rem',
                      textAlign: 'center',
                    }}
                  />{' '}
                  CONDITION(S)
                </button>
              )}
          </div>
        </div>
        {getMoreConditionsErrorUI()}
      </div>
      <div
        ref={conditionsContainerRef}
        style={{ height: '45vh', width: '100%', overflow: 'auto' }}
      >
        {getCondtionsView()}
      </div>
    </>
  );
}
